import { DETECTION_TYPES_CONFIG } from 'constants/av-review';
import { VERDICT_STATES, VIEWING_THRESHOLD } from './constants';

export const isInvalidReasonRequired = detectionType =>
  (DETECTION_TYPES_CONFIG[detectionType]?.invalidReasons?.length || 0) > 0;

export const getInvalidReasonLabel = (detectionType, value) => {
  if (!value) return null;
  const options = DETECTION_TYPES_CONFIG[detectionType]?.invalidReasons || [];
  const match = options.find(opt => opt.value === value);
  return match ? match.label : value;
};

export const getDetectionSummary = (events = []) => {
  const { reviewed, hasValid } = events.reduce(
    (acc, e) => {
      if (e.is_valid !== null && e.is_valid !== undefined) {
        acc.reviewed++;
        if (e.is_valid === true) acc.hasValid = true;
      }
      return acc;
    },
    { reviewed: 0, hasValid: false },
  );

  const total = events.length;
  let verdict = VERDICT_STATES.PENDING;
  if (reviewed > 0 && reviewed < total) verdict = VERDICT_STATES.REVIEWING;
  else if (reviewed === total && total > 0)
    verdict = hasValid ? VERDICT_STATES.VALID : VERDICT_STATES.INVALID;

  return { progress: { reviewed, total }, verdict };
};

export const isViewingThresholdMet = event => {
  if (!event?.event_data) return false;
  const viewedCount = event.event_data.filter(c => c.is_viewed).length;
  const totalChunks = event.event_data.length;
  const minRequired =
    totalChunks <= VIEWING_THRESHOLD.SMALL_MAX
      ? VIEWING_THRESHOLD.SMALL_MIN_VIEWED
      : VIEWING_THRESHOLD.LARGE_MIN_VIEWED;
  return viewedCount >= minRequired;
};

export const groupEventsByType = (events = []) => {
  const typeOrder = Object.keys(DETECTION_TYPES_CONFIG);

  const grouped = events.reduce((acc, event) => {
    const { event_type } = event;
    acc[event_type] = [...(acc[event_type] || []), event];
    return acc;
  }, {});

  return typeOrder.reduce((result, type) => {
    if (grouped[type]) {
      const typeEvents = grouped[type];
      const { progress, verdict } = getDetectionSummary(typeEvents);
      result[type] = { type, events: typeEvents, progress, verdict };
    }
    return result;
  }, {});
};

export const formatDurationMs = ms => {
  if (ms == null || isNaN(ms)) return '-';
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins > 0) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  return `${secs}s`;
};

export const getNextVideoUrl = (events, eventIndex, chunkIndex) => {
  if (!events?.length) return null;

  const currentEvent = events[eventIndex];
  if (!currentEvent?.event_data) return null;

  for (let i = chunkIndex + 1; i < currentEvent.event_data.length; i++) {
    const chunk = currentEvent.event_data[i];
    if (chunk.evidence_type === 'video' && chunk.evidence_url) return chunk.evidence_url;
  }

  for (let e = eventIndex + 1; e < events.length; e++) {
    const event = events[e];
    if (!event?.event_data) continue;
    for (let i = 0; i < event.event_data.length; i++) {
      const chunk = event.event_data[i];
      if (chunk.evidence_type === 'video' && chunk.evidence_url)
        return chunk.evidence_url;
    }
  }

  return null;
};

export const getNextUnreviewedDetection = (detections, currentType) => {
  const types = Object.keys(detections);
  const currentIndex = types.indexOf(currentType);

  for (let i = 1; i <= types.length; i++) {
    const nextType = types[(currentIndex + i) % types.length];
    const nextVerdict = detections[nextType].verdict;
    if (
      nextType !== currentType &&
      (nextVerdict === VERDICT_STATES.PENDING || nextVerdict === VERDICT_STATES.REVIEWING)
    ) {
      return nextType;
    }
  }
  return null;
};

export const downloadVideo = (url, { onProgress, signal } = {}) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';

    if (onProgress) {
      xhr.onprogress = event => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
    }

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 206) {
        const raw = xhr.response;
        const blob = raw.type.startsWith('video/')
          ? raw
          : new Blob([raw], { type: 'video/webm' });
        resolve(URL.createObjectURL(blob));
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send();
  });

export const VIDEO_CACHE_MAX_SIZE = 5;

export const setVideoCacheEntry = ({ cache, url, blobUrl, protectedUrl }) => {
  if (!cache.has(url) && cache.size >= VIDEO_CACHE_MAX_SIZE) {
    // Evict the oldest entry by insertion order. If the oldest is the currently-active
    // blob, evict the second-oldest instead — revoking the active blob would kill the
    // <video> element's src and leave it stuck.
    const iter = cache.keys();
    let keyToEvict = iter.next().value;
    if (keyToEvict === protectedUrl) keyToEvict = iter.next().value;
    if (keyToEvict) {
      URL.revokeObjectURL(cache.get(keyToEvict));
      cache.delete(keyToEvict);
    }
  }
  cache.set(url, blobUrl);
};
